import { useState, useEffect } from 'react'
import { getConteudoChave, putConteudoChave } from '@/api/adminApi'
import { ADMIN_KEYS, CONTEUDO_API_KEYS } from '@/utils/adminStorage'
import {
  parseContatos,
  serializeContatos,
  REDE_TIPOS,
  getRedePlaceholder,
  type RedeSocialItem,
  type RedeTipo,
} from '@/utils/contatos'
import { AdminPage, AdminPanel } from '@/components/Admin'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Admin.module.css'

const REDE_PADRAO: RedeTipo = 'instagram'

function emptyRede(): RedeSocialItem {
  return { rede: REDE_PADRAO, valor: '' }
}

export function AdminContatosPage() {
  const [telefone, setTelefone] = useState('')
  const [email, setEmail] = useState('')
  const [morada, setMorada] = useState('')
  const [redes, setRedes] = useState<RedeSocialItem[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { notifySaved, notifyError } = useSaveFeedback()
  const apiChave = CONTEUDO_API_KEYS[ADMIN_KEYS.contatos]

  useEffect(() => {
    let cancelled = false
    setError(null)
    getConteudoChave(apiChave)
      .then((v) => {
        if (cancelled) return
        const d = parseContatos(v ?? null)
        setTelefone(d.telefone ?? '')
        setEmail(d.email ?? '')
        setMorada(d.morada ?? '')
        setRedes(d.redes.length ? d.redes : [])
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Erro ao carregar')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [apiChave])

  const updateRede = (index: number, patch: Partial<RedeSocialItem>) => {
    setRedes((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }

  const addRede = () => setRedes((prev) => [...prev, emptyRede()])

  const removeRede = (index: number) => setRedes((prev) => prev.filter((_, i) => i !== index))

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    const payload = serializeContatos({
      telefone,
      email,
      morada,
      redes: redes.filter((r) => r.valor.trim()),
    })
    try {
      await putConteudoChave(apiChave, payload)
      const parsed = parseContatos(payload)
      setRedes(parsed.redes)
      notifySaved('Contactos guardados.')
    } catch (err) {
      notifyError(err, 'Erro ao salvar')
      setError(err instanceof Error ? err.message : 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <AdminPage
      title="Contatos"
      subtitle="Telefone, e-mail, redes e morada exibidos na área de contactos do app."
      loading={loading}
      error={error}
    >
      <form onSubmit={handleSalvar} className={styles.adminFormStack}>
        <AdminPanel
          title="Dados de contacto"
          footer={
            <button type="submit" className={styles.btnSalvar} disabled={saving}>
              {saving ? 'A guardar…' : 'Guardar contactos'}
            </button>
          }
        >
          <div className={styles.contatosGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="ct-tel">Telefone</label>
              <input
                id="ct-tel"
                type="tel"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                placeholder="+351 123 456 789"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="ct-email">E-mail</label>
              <input
                id="ct-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contacto@exemplo.pt"
              />
            </div>
            <div className={`${styles.formGroup} ${styles.spanFull}`}>
              <label htmlFor="ct-morada">Morada</label>
              <input
                id="ct-morada"
                type="text"
                value={morada}
                onChange={(e) => setMorada(e.target.value)}
                placeholder="Endereço completo"
              />
            </div>
          </div>

          <div className={styles.contatosRedesSection}>
            <h3 className={styles.contatosRedesTitle}>
              Redes sociais
              {redes.length > 0 && (
                <span className={styles.listCountBadge}>{redes.length}</span>
              )}
            </h3>
            <p className={styles.panelDesc}>
              Adicione só as redes que quiser — no app aparecem apenas as que tiverem link ou conta
              preenchidos.
            </p>

            {redes.length === 0 && (
              <p className={styles.listEmpty}>Nenhuma rede adicionada.</p>
            )}

            {redes.length > 0 && (
              <div className={styles.listEditorScroll}>
                <div
                  className={`${styles.listEditorHead} ${styles['listEditorHead--redeSocial']}`}
                  aria-hidden
                >
                  <span>#</span>
                  <span>Rede</span>
                  <span>Link ou conta</span>
                  <span />
                </div>

                {redes.map((item, i) => (
                  <div
                    key={i}
                    className={`${styles.vipListItem} ${styles['vipListItem--redeSocial']}`}
                  >
                    <span className={styles.rowIndex}>{i + 1}</span>
                    <select
                      value={item.rede}
                      onChange={(e) =>
                        updateRede(i, {
                          rede: e.target.value as RedeTipo,
                          ...(e.target.value !== 'outro' ? { nome: undefined } : {}),
                        })
                      }
                      aria-label={`Tipo de rede ${i + 1}`}
                    >
                      {REDE_TIPOS.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <div className={styles.redeValorCol}>
                      {item.rede === 'outro' && (
                        <input
                          type="text"
                          value={item.nome ?? ''}
                          onChange={(e) => updateRede(i, { nome: e.target.value })}
                          placeholder="Nome a mostrar (ex.: Pinterest)"
                          aria-label={`Nome da rede ${i + 1}`}
                        />
                      )}
                      <input
                        type="text"
                        value={item.valor}
                        onChange={(e) => updateRede(i, { valor: e.target.value })}
                        placeholder={getRedePlaceholder(item.rede)}
                        aria-label={`Link ou conta ${i + 1}`}
                      />
                    </div>
                    <button
                      type="button"
                      className={styles.vipBtnRemove}
                      onClick={() => removeRede(i)}
                      aria-label="Remover rede"
                    >
                      Remover
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.vipListAdd}>
              <button type="button" className={styles.vipBtnAdd} onClick={addRede}>
                + Adicionar rede social
              </button>
            </div>
          </div>
        </AdminPanel>
      </form>
    </AdminPage>
  )
}
